// Customers tab: customer list + edit form (rates, aliases, billing settings).
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState } from 'react';
import { useApi, postJson, putJson, delJson } from '../../api.js';
import CertRecipientsPanel from './CertRecipientsPanel.jsx';
import RateHistoryPanel from './RateHistoryPanel.jsx';

export default function CustomersPanel({ canWrite = true }) {
  // Data: customers, all registered plates, statement contacts, and the "suggested new customers" list.
  const { data, error, loading, refresh } = useApi('/api/customers');
  const platesApi = useApi('/api/plates');
  const contactsApi = useApi('/api/customer-contacts');
  const suggestedApi = useApi('/api/customers/suggested');
  const [filter, setFilter] = useState('');
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  // Collapsed by default when empty; expanded when there's something to add.
  const [suggestedOpen, setSuggestedOpen] = useState(true);
  const [plateEdit, setPlateEdit] = useState(null);   // { plate, tankSize, comment, _isNew } when an inline row is being edited
  const [plateBusy, setPlateBusy] = useState(false);

  // Plates inside the customer pop-up: save / delete one plate row.
  async function savePlate() {
    if (!plateEdit || !plateEdit.plate) { alert('Plate is required.'); return; }
    setPlateBusy(true);
    try {
      await putJson('/api/plates', {
        plate: plateEdit.plate,
        tankSize: plateEdit.tankSize === '' || plateEdit.tankSize == null ? null : Number(plateEdit.tankSize),
        customer: edit.display_name,
        comment: plateEdit.comment || ''
      });
      setPlateEdit(null);
      platesApi.refresh();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setPlateBusy(false); }
  }
  async function delPlate(plate) {
    if (!confirm('Remove plate ' + plate + ' from ' + edit.display_name + '?')) return;
    setPlateBusy(true);
    try { await delJson('/api/plates?plate=' + encodeURIComponent(plate)); platesApi.refresh(); }
    catch (e) { alert('Delete failed: ' + e.message); }
    finally { setPlateBusy(false); }
  }
  // Lookups: plates and contacts grouped by customer name (for the counts in the table and the pop-up lists).
  // Group plates by customer_name (lowercased) for fast per-row count lookup.
  const platesByCustomer = (() => {
    const m = new Map();
    const ps = (platesApi.data && platesApi.data.plates) || [];
    for (const p of ps) {
      const k = String(p.customer || '').toLowerCase().trim();
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    }
    return m;
  })();
  // Same shape for contacts
  const contactsByCustomer = (() => {
    const m = new Map();
    const cs = (contactsApi.data && contactsApi.data.contacts) || [];
    for (const c of cs) {
      const k = String(c.customer || '').toLowerCase().trim();
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    }
    return m;
  })();
  // Contacts inside the customer pop-up: save / delete one statement recipient.
  const [contactEdit, setContactEdit] = useState(null);
  const [contactBusy, setContactBusy] = useState(false);
  async function saveContact() {
    if (!contactEdit || !contactEdit.email) { alert('Email is required.'); return; }
    if (!contactEdit.email.includes('@')) { alert('Invalid email.'); return; }
    setContactBusy(true);
    try {
      await putJson('/api/customer-contacts', {
        id: contactEdit.id,
        customer: edit.display_name,
        email: contactEdit.email.trim().toLowerCase(),
        name: contactEdit.name || null,
        role: contactEdit.role || null,
        is_primary: !!contactEdit.is_primary,
        notes: contactEdit.notes || null
      });
      setContactEdit(null);
      contactsApi.refresh();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setContactBusy(false); }
  }
  async function delContact(id) {
    if (!confirm('Remove this contact?')) return;
    setContactBusy(true);
    try {
      await delJson('/api/customer-contacts/' + id);
      contactsApi.refresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
      // Do NOT call refresh() on failure — keeps the deleted-looking row
      // visible so the user knows the operation didn't take effect.
    }
    finally { setContactBusy(false); }
  }

  if (loading) return <div className="loading">Loading…</div>;
  if (error)   return <div className="error">Error: {error}</div>;
  // Customer list after the filter box.
  const customers = (data && data.customers) || [];
  const f = filter.toLowerCase().trim();
  const shown = !f ? customers : customers.filter(c =>
    (c.display_name   || '').toLowerCase().includes(f) ||
    (c.qbo_full_name  || '').toLowerCase().includes(f) ||
    (c.billed_to      || '').toLowerCase().includes(f));

  // Actions: new blank customer, save (incl. rate change history), delete.
  function blank() {
    return { display_name:'', qbo_full_name:'', billed_to:'', small_rate:'', regular_rate:'', oversize18_rate:'', oversize28_rate:'', drill_mud_rate:'', drill_mud_18_rate:'', drill_mud_28_rate:'', wants_excel_statement: false, reporting_group:'', notes:'', effective_from: new Date().toISOString().slice(0,10), rate_notes: '', address:'', mailing_address:'', phone:'', transporter_type:'hydrovac' };
  }
  async function save() {
    if (!edit || !edit.display_name) { alert('Display name is required.'); return; }
    // Determine whether rate fields differ from what the customer already has.
    // If so, they become a new customer_rates row keyed on effective_from.
    const RATE_FIELDS = ['small_rate','regular_rate','oversize18_rate','oversize28_rate','drill_mud_rate','drill_mud_18_rate','drill_mud_28_rate'];
    const original = customers.find(c => c.canonical_name === edit.canonical_name) || {};
    function normRate(v) { return (v === '' || v == null) ? null : Number(v); }
    let anyRateChanged = false;
    for (const f of RATE_FIELDS) {
      if (normRate(edit[f]) !== normRate(original[f])) { anyRateChanged = true; break; }
    }
    const today = new Date().toISOString().slice(0,10);
    const effective = (edit.effective_from || '').trim();
    // If any rate changed, we REQUIRE explicit effective_from + rate_notes.
    // The audit trail is only useful if it captures WHY the rate changed.
    if (anyRateChanged) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(effective)) {
        alert('Rate change effective date is required (YYYY-MM-DD).');
        return;
      }
      if (!edit.rate_notes || !String(edit.rate_notes).trim()) {
        alert('Please add a rate change note explaining why the rate is changing (contract update, negotiation, etc.). This is the audit trail.');
        return;
      }
    }
    // Backdate impact preview + user confirmation.
    if (anyRateChanged && effective < today) {
      const goAhead = confirm(
        'Backdate warning\n\n' +
        'You are setting rate change effective ' + effective + ', which is before today.\n\n' +
        'Every load dated on or after ' + effective + ' will re-price on the next Balances refresh, and any statements you regenerate for cycles after that date will show the new numbers.\n\n' +
        'Continue?'
      );
      if (!goAhead) return;
    }
    setBusy(true);
    try {
      // Save non-rate fields first so the customer row exists with the right
      // display_name/billed_to etc. Rates on the /api/customers PUT are still
      // accepted but treated as the "current cache" — the source of truth is
      // now customer_rates.
      await putJson('/api/customers', edit);
      // Then create a new rate history row if anything changed.
      if (anyRateChanged) {
        const body = {
          customer: edit.canonical_name || edit.display_name,
          effective_from: effective,
          notes: edit.rate_notes || null
        };
        for (const f of RATE_FIELDS) body[f] = normRate(edit[f]);
        const resp = await postJson('/api/settings/customer-rates', body);
        if (resp && resp.impact && (resp.impact.loadsImpacted > 0 || resp.impact.closedSnapshotsImpacted > 0)) {
          alert(
            'Rate change saved. Backdate impact:\n' +
            '· ' + resp.impact.loadsImpacted + ' load(s) will re-price\n' +
            '· ' + resp.impact.closedSnapshotsImpacted + ' closed cycle snapshot(s) affected — regenerate statements for those cycles if you need updated PDFs.'
          );
        }
      }
      setEdit(null);
      refresh();
      // Refresh suggested list too -- newly-added customer should
      // drop off the "Suggested new customers" banner.
      suggestedApi.refresh();
    } catch (e) {
      let msg = e.message || String(e);
      try {
        const j = JSON.parse(msg.split(' ').slice(1).join(' '));
        if (j && j.error === 'backdate_conflict') {
          msg = 'Cannot save: ' + j.message;
        }
      } catch (_) {}
      alert('Save failed: ' + msg);
    }
    finally { setBusy(false); }
  }
  async function del(c) {
    if (!confirm('Delete customer "' + c.display_name + '"? This removes its rates / QBO name / billed_to. Contacts on this customer are NOT deleted.')) return;
    setBusy(true);
    try { await delJson('/api/customers/' + encodeURIComponent(c.canonical_name)); refresh(); }
    catch (e) { alert('Delete failed: ' + e.message); }
    finally { setBusy(false); }
  }

  // Billed-to autocomplete names and the suggested-customers banner data.
  // Build the autocomplete list for billed_to. Uses known_names from the
  // API (every customer name we've seen across customers / tips_excel /
  // tips_google) so finance can pick a parent like 'Metrocon ReadyMix Corp.'
  // that has QBO invoices but no customers row yet — saving will auto-create it.
  const knownNames = (data && data.known_names) || customers.map(c => c.display_name);
  const allNames = (knownNames || []).filter(Boolean).sort((a,b)=>a.localeCompare(b));

  const suggestedList = (suggestedApi.data && suggestedApi.data.suggested) || [];
  const activityCutoff = suggestedApi.data && suggestedApi.data.activityCutoff;

  // Screen: suggested-customers banner, toolbar, customer table, edit pop-up.
  return (
    <div className="card" style={{ padding: '0.75rem' }}>
      {/* Suggested new customers: names seen on recent Google-sheet tips that are not customers yet (one click to add). */}
      {canWrite && suggestedList.length > 0 && (
        <div style={{ marginBottom: '0.75rem', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 4, padding: '0.5rem 0.75rem' }}>
          <button onClick={() => setSuggestedOpen(o => !o)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.9em', color: '#854d0e', fontWeight: 600 }}>
            {suggestedOpen ? '▾' : '▸'} Suggested new customers ({suggestedList.length})
            <span style={{ fontWeight: 400, color: '#a16207', marginLeft: 6 }}>
              — recent Google Sheet activity{activityCutoff ? ' since ' + activityCutoff : ''}, not yet in the customers list
            </span>
          </button>
          {suggestedOpen && (
            <div style={{ marginTop: '0.5rem', maxHeight: '18em', overflowY: 'auto' }}>
              <table className="datatable" style={{ margin: 0, fontSize: '0.9em' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#fef3c7' }}>Name</th>
                    <th style={{ background: '#fef3c7' }}>Source</th>
                    <th style={{ background: '#fef3c7' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {suggestedList.map(s => (
                    <tr key={s.name}>
                      <td><strong>{s.name}</strong></td>
                      <td>
                        {s.sources.includes('qbo') && (
                          <span style={{ background: '#dbeafe', color: '#1e40af', fontSize: '0.75em', padding: '1px 6px', borderRadius: 3, marginRight: 4 }}>QBO</span>
                        )}
                        {(s.sources.includes('sheet') || s.sources.includes('google') || s.sources.includes('excel')) && (
                          <span style={{ background: '#dcfce7', color: '#166534', fontSize: '0.75em', padding: '1px 6px', borderRadius: 3, marginRight: 4 }}>Sheet</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="refresh-btn small"
                                onClick={() => {
                                  // Pre-populate the Add modal. display_name gets
                                  // the sheet name if available (typed by ops on
                                  // the form); qbo_full_name gets the QBO
                                  // canonical spelling.
                                  const b = blank();
                                  b.display_name   = s.sheetName || s.qboName || s.name;
                                  b.qbo_full_name  = s.qboName   || (s.sources.includes('qbo') ? s.name : '');
                                  b.reporting_group = b.display_name;
                                  setEdit(b);
                                }}
                                style={{ background: '#15803d', padding: '3px 12px' }}>
                          + Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Toolbar: filter, + Add customer, count. */}
      <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.5rem' }}>
        <input type="text" placeholder="Filter by name / QB name / billed-to…" value={filter} onChange={e=>setFilter(e.target.value)}
               style={{ flex:1, padding:'4px 8px' }} />
        {canWrite && <button className="refresh-btn" onClick={() => setEdit(blank())} style={{ background:'#15803d' }}>+ Add customer</button>}
        <span className="small" style={{ color:'#666' }}>{shown.length}/{customers.length} customers</span>
      </div>
      {/* Customer table: one row per customer with rates, billed-to, plates and contacts counts. */}
      <table className="datatable">
        <thead><tr>
          <th>Customer (display name)</th>
          <th>QBO full name</th>
          <th>Billed to</th>
          <th className="num">Plates</th>
          <th className="num">Contacts</th>
          <th className="num">Small</th>
          <th className="num">Regular</th>
          <th className="num">18m³</th>
          <th className="num">28m³</th>
          <th className="num" title="Drill mud (regular tank)">DM</th>
          <th className="num" title="Drill mud 18m³">DM-18</th>
          <th className="num" title="Drill mud 28m³">DM-28</th>
          <th>Notes</th>
          <th></th>
        </tr></thead>
        <tbody>
        {shown.map(c => (
          <tr key={c.canonical_name}>
            <td><strong>{c.display_name}</strong></td>
            <td className="small" style={{color:'#475569'}}>{c.qbo_full_name || '—'}</td>
            <td className="small">{c.billed_to || '—'}</td>
            <td className="num">{(platesByCustomer.get(String(c.display_name || '').toLowerCase()) || []).length || '—'}</td>
            <td className="num">{(contactsByCustomer.get(String(c.display_name || '').toLowerCase()) || []).length || '—'}</td>
            <td className="num">{c.small_rate      != null ? '$' + Number(c.small_rate).toLocaleString()      : '—'}</td>
            <td className="num">{c.regular_rate    != null ? '$' + Number(c.regular_rate).toLocaleString()    : '—'}</td>
            <td className="num">{c.oversize18_rate != null ? '$' + Number(c.oversize18_rate).toLocaleString() : '—'}</td>
            <td className="num">{c.oversize28_rate != null ? '$' + Number(c.oversize28_rate).toLocaleString() : '—'}</td>
            <td className="num">{c.drill_mud_rate    != null ? '$' + Number(c.drill_mud_rate).toLocaleString()    : '—'}</td>
            <td className="num">{c.drill_mud_18_rate != null ? '$' + Number(c.drill_mud_18_rate).toLocaleString() : '—'}</td>
            <td className="num">{c.drill_mud_28_rate != null ? '$' + Number(c.drill_mud_28_rate).toLocaleString() : '—'}</td>
            <td className="small" style={{maxWidth:'14em', overflow:'hidden', textOverflow:'ellipsis'}}>{c.notes || ''}</td>
            <td style={{whiteSpace:'nowrap'}}>
              <button className="refresh-btn small" onClick={() => setEdit({ ...c })}>Edit</button>
              <button className="refresh-btn small" style={{background:'#b91c1c', marginLeft:4}} onClick={() => del(c)}>Delete</button>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
      {/* Edit pop-up. Sections top to bottom: names & billed-to, reporting group, address/phone/transporter type, standard rates, drill-mud rates, Excel-attachment option, rate change details + history, notes, plates, contacts, certificate recipients. */}
      {edit && (
        <div className="modal-backdrop" onClick={() => setEdit(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '52em' }}>
            <h3 style={{marginTop:0}}>{edit.canonical_name ? 'Edit ' + edit.display_name : 'New customer'}</h3>
            {/* Identity block — display name + QBO name side-by-side; billed-to spans full width because the placeholder text is long. */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem 0.75rem'}}>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small" style={{fontWeight:600}}>Display name</span>
                <input value={edit.display_name||''} onChange={e=>setEdit({...edit, display_name:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small" style={{fontWeight:600}}>QBO full name</span>
                <input value={edit.qbo_full_name||''} onChange={e=>setEdit({...edit, qbo_full_name:e.target.value})} placeholder="(only if different from display name)" style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small" style={{fontWeight:600}}>Billed to (parent)</span>
                <input list="customer-parent-options" value={edit.billed_to||''}
                       onChange={e=>setEdit({...edit, billed_to:e.target.value})}
                       placeholder="(blank if this customer is billed directly)"
                       style={{width:'100%', boxSizing:'border-box'}} />
                <datalist id="customer-parent-options">{allNames.filter(n => n !== edit.display_name).map(n => <option key={n} value={n} />)}</datalist>
              </label>
              <div style={{gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small" style={{fontWeight:600}}>Reporting group</span>
                {(() => {
                  // Full list of every distinct reporting_group already in
                  // the customers table. Sorted case-insensitively. If the
                  // currently-edited value isn't in the list (typed a brand
                  // new one), we still include it so the select stays in
                  // sync with the input.
                  const distinct = Array.from(new Set(
                    customers.map(c => (c.reporting_group || '').trim()).filter(Boolean)
                  )).sort((a, b) => a.localeCompare(b));
                  const current = (edit.reporting_group || '').trim();
                  if (current && !distinct.includes(current)) distinct.unshift(current);
                  return (
                    <div style={{ display:'flex', gap:'0.4rem', alignItems:'center' }}>
                      <select value={current || ''}
                              onChange={e => setEdit({ ...edit, reporting_group: e.target.value })}
                              style={{ flex:1, padding:'0.35rem 0.5rem', boxSizing:'border-box',
                                        border:'1px solid #cbd5e1', borderRadius:3, background:'#fff' }}>
                        <option value="">(defaults to display name)</option>
                        {distinct.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <input value={edit.reporting_group || ''}
                             onChange={e => setEdit({ ...edit, reporting_group: e.target.value })}
                             placeholder="or type a new one"
                             style={{ flex:1, boxSizing:'border-box' }} />
                    </div>
                  );
                })()}
                <span className="small" style={{ color:'#64748b' }}>
                  Pick an existing group from the dropdown, or type a new name to create one. Rolls this customer up in every dashboard report except Balances. Billing and statements stay per-customer.
                </span>
              </div>
            </div>

            {/* Compliance info — feeds the compliance certificate snapshot. */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem 0.75rem', marginTop:'0.5rem', padding:'0.6rem 0.75rem', background:'#f0fdfa', borderRadius:4, border:'1px solid #99f6e4'}}>
              <div style={{gridColumn:'1 / -1', fontSize:'0.85em', color:'#115e59', fontWeight:600}}>
                Compliance info (printed on the compliance certificate)
              </div>
              <label style={{gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Address</span>
                <input value={edit.address||''} onChange={e=>setEdit({...edit, address:e.target.value})} placeholder="street, city, province, postal" style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Mailing address (only if different)</span>
                <input value={edit.mailing_address||''} onChange={e=>setEdit({...edit, mailing_address:e.target.value})} placeholder="(blank = same as address)" style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Phone</span>
                <input value={edit.phone||''} onChange={e=>setEdit({...edit, phone:e.target.value})} placeholder="905-555-1234" style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Transporter type</span>
                <select value={edit.transporter_type||'hydrovac'} onChange={e=>setEdit({...edit, transporter_type:e.target.value})}
                        style={{width:'100%', boxSizing:'border-box', padding:'0.35rem 0.5rem', border:'1px solid #cbd5e1', borderRadius:3, background:'#fff'}}>
                  <option value="hydrovac">Hydrovac company / transporter</option>
                  <option value="generator">Source material generator site</option>
                </select>
              </label>
            </div>

            {/* Standard rates — 2x2 grid, uniform input widths. */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem 0.75rem', marginTop:'0.5rem', padding:'0.6rem 0.75rem', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb'}}>
              <div style={{gridColumn:'1 / -1', fontSize:'0.85em', color:'#334155', fontWeight:600}}>
                Standard rates ($ / load)
              </div>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Small</span>
                <input type="number" step="0.01" value={edit.small_rate ?? ''} onChange={e=>setEdit({...edit, small_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Regular</span>
                <input type="number" step="0.01" value={edit.regular_rate ?? ''} onChange={e=>setEdit({...edit, regular_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Oversize 18m³</span>
                <input type="number" step="0.01" value={edit.oversize18_rate ?? ''} onChange={e=>setEdit({...edit, oversize18_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Oversize 28m³</span>
                <input type="number" step="0.01" value={edit.oversize28_rate ?? ''} onChange={e=>setEdit({...edit, oversize28_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
            </div>

            {/* Drill mud rates — 3 columns for regular/18/28; matches the standard-rates card style so both sections feel like one form. */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.6rem 0.75rem', marginTop:'0.5rem', padding:'0.6rem 0.75rem', background:'#fffbeb', borderRadius:4, border:'1px solid #fde68a'}}>
              <div style={{gridColumn:'1 / -1', fontSize:'0.85em', color:'#92400e', fontWeight:600}}>
                Drill mud rates (blank = use defaults: $850 / $1,275 / $1,985)
              </div>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">Regular</span>
                <input type="number" step="0.01" placeholder="850" value={edit.drill_mud_rate ?? ''} onChange={e=>setEdit({...edit, drill_mud_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">18m³</span>
                <input type="number" step="0.01" placeholder="1275" value={edit.drill_mud_18_rate ?? ''} onChange={e=>setEdit({...edit, drill_mud_18_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
              <label style={{display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                <span className="small">28m³</span>
                <input type="number" step="0.01" placeholder="1985" value={edit.drill_mud_28_rate ?? ''} onChange={e=>setEdit({...edit, drill_mud_28_rate:e.target.value})} style={{width:'100%', boxSizing:'border-box'}} />
              </label>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginTop:'0.5rem', padding:'0.5rem 0.75rem', background:'#eef2ff', border:'1px solid #c7d2fe', borderRadius:4, cursor:'pointer' }}>
              <input type="checkbox" checked={!!edit.wants_excel_statement}
                     onChange={e => setEdit({ ...edit, wants_excel_statement: e.target.checked })} />
              <span><strong>📎 Customer wants Excel attachment with statements</strong> — sends the raw load data + summary pivot alongside the PDF.</span>
            </label>
            {/* Effective date + rate notes — every rate change writes a new history row.
                Backdating is allowed (recomputes past bills) but triggers a warning
                on Save if the date is before today. Server rejects if the date is not
                strictly greater than the customer's most recent history row. */}
            <div style={{ display:'grid', gridTemplateColumns:'12em 1fr', gap:'0.5rem 0.75rem', alignItems:'center', marginTop:'0.5rem', padding:'0.5rem 0.75rem', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:4 }}>
              <div style={{ gridColumn:'1 / -1', fontSize:'0.85em', color:'#1e3a8a', fontWeight:600 }}>
                Rate change details (writes a new history row)
              </div>
              <label>Effective from</label>
              <input type="date" value={edit.effective_from || ''} onChange={e => setEdit({ ...edit, effective_from: e.target.value })} />
              <label>Rate change notes</label>
              <input type="text" value={edit.rate_notes || ''} onChange={e => setEdit({ ...edit, rate_notes: e.target.value })} placeholder="Why the change? (contract update, negotiation, etc.)" />
            </div>
            {edit.canonical_name && <RateHistoryPanel canonicalName={edit.canonical_name} />}
            <label style={{display:'block', marginTop:'0.5rem'}}>Notes
              <textarea rows={3} value={edit.notes||''} onChange={e=>setEdit({...edit, notes:e.target.value})} style={{width:'100%'}} />
            </label>
            {/* Plates registered to this customer (add / edit / remove inline). */}
            {edit.canonical_name && (() => {
              const myPlates = platesByCustomer.get(String(edit.display_name || '').toLowerCase()) || [];
              return (
                <div style={{marginTop:'0.6rem', borderTop:'1px solid #e5e7eb', paddingTop:'0.4rem'}}>
                  <div style={{display:'flex', alignItems:'baseline', gap:'0.5rem'}}>
                    <strong>Plates registered to this customer</strong>
                    <span className="small" style={{color:'#666'}}>{myPlates.length === 0 ? 'none yet' : myPlates.length + ' plate' + (myPlates.length === 1 ? '' : 's')}</span>
                    <button className="refresh-btn small" style={{marginLeft:'auto', background:'#15803d'}}
                            onClick={() => setPlateEdit({ plate:'', tankSize:'', comment:'', _isNew:true })}
                            disabled={!!plateEdit}>+ Add plate</button>
                  </div>
                  {(myPlates.length > 0 || plateEdit) && (
                    <div style={{maxHeight:'14em', overflowY:'auto', marginTop:'0.3rem', fontSize:'0.85em'}}>
                      <table className="datatable" style={{margin:0}}>
                        <thead><tr><th>Plate</th><th className="num">Tank size (m³)</th><th>Notes</th><th style={{width:'8em'}}></th></tr></thead>
                        <tbody>
                          {plateEdit && plateEdit._isNew && (
                            <tr style={{background:'#f0fdf4'}}>
                              <td><input value={plateEdit.plate} onChange={e=>setPlateEdit({...plateEdit, plate:e.target.value.toUpperCase()})} placeholder="e.g. AS12345" style={{width:'7em', fontFamily:'monospace'}} /></td>
                              <td className="num"><input type="number" step="0.1" value={plateEdit.tankSize} onChange={e=>setPlateEdit({...plateEdit, tankSize:e.target.value})} style={{width:'5em'}} /></td>
                              <td><input value={plateEdit.comment} onChange={e=>setPlateEdit({...plateEdit, comment:e.target.value})} style={{width:'100%'}} /></td>
                              <td>
                                <button className="refresh-btn small" onClick={savePlate} disabled={plateBusy}>Save</button>
                                <button className="refresh-btn small" style={{background:'#94a3b8', marginLeft:4}} onClick={() => setPlateEdit(null)} disabled={plateBusy}>Cancel</button>
                              </td>
                            </tr>
                          )}
                          {myPlates.map(p => plateEdit && !plateEdit._isNew && plateEdit.plate === p.plate ? (
                            <tr key={p.plate} style={{background:'#fef3c7'}}>
                              <td><code>{p.plate}</code></td>
                              <td className="num"><input type="number" step="0.1" value={plateEdit.tankSize ?? ''} onChange={e=>setPlateEdit({...plateEdit, tankSize:e.target.value})} style={{width:'5em'}} /></td>
                              <td><input value={plateEdit.comment ?? ''} onChange={e=>setPlateEdit({...plateEdit, comment:e.target.value})} style={{width:'100%'}} /></td>
                              <td>
                                <button className="refresh-btn small" onClick={savePlate} disabled={plateBusy}>Save</button>
                                <button className="refresh-btn small" style={{background:'#94a3b8', marginLeft:4}} onClick={() => setPlateEdit(null)} disabled={plateBusy}>Cancel</button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={p.plate}>
                              <td><code>{p.plate}</code></td>
                              <td className="num">{p.tankSize ?? '—'}</td>
                              <td className="small">{p.comment || ''}</td>
                              <td>
                                <button className="refresh-btn small" onClick={() => setPlateEdit({ ...p, _isNew:false })} disabled={!!plateEdit || plateBusy}>Edit</button>
                                <button className="refresh-btn small" style={{background:'#b91c1c', marginLeft:4}} onClick={() => delPlate(p.plate)} disabled={!!plateEdit || plateBusy}>×</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Statement contacts for this customer (who receives statements). */}
            {edit.canonical_name && (() => {
              const myContacts = contactsByCustomer.get(String(edit.display_name || '').toLowerCase()) || [];
              return (
                <div style={{marginTop:'0.6rem', borderTop:'1px solid #e5e7eb', paddingTop:'0.4rem'}}>
                  <div style={{display:'flex', alignItems:'baseline', gap:'0.5rem'}}>
                    <strong>Contacts (statement recipients)</strong>
                    <span className="small" style={{color:'#666'}}>{myContacts.length === 0 ? 'none' : myContacts.length + ' contact' + (myContacts.length === 1 ? '' : 's')}</span>
                    <button className="refresh-btn small" style={{marginLeft:'auto', background:'#15803d'}}
                            onClick={() => setContactEdit({ email:'', name:'', role:'', is_primary: myContacts.length === 0, notes:'' })}
                            disabled={!!contactEdit}>+ Add contact</button>
                  </div>
                  {(myContacts.length > 0 || contactEdit) && (
                    <div style={{maxHeight:'12em', overflowY:'auto', marginTop:'0.3rem', fontSize:'0.85em'}}>
                      <table className="datatable" style={{margin:0}}>
                        <thead><tr><th>Email</th><th>Name</th><th>Role</th><th className="num">Primary</th><th style={{width:'8em'}}></th></tr></thead>
                        <tbody>
                          {contactEdit && !contactEdit.id && (
                            <tr style={{background:'#f0fdf4'}}>
                              <td><input value={contactEdit.email} onChange={e=>setContactEdit({...contactEdit, email:e.target.value})} placeholder="name@example.com" style={{width:'100%'}} /></td>
                              <td><input value={contactEdit.name||''} onChange={e=>setContactEdit({...contactEdit, name:e.target.value})} style={{width:'100%'}} /></td>
                              <td><input value={contactEdit.role||''} onChange={e=>setContactEdit({...contactEdit, role:e.target.value})} placeholder="A/P, Ops…" style={{width:'100%'}} /></td>
                              <td className="num"><input type="checkbox" checked={!!contactEdit.is_primary} onChange={e=>setContactEdit({...contactEdit, is_primary:e.target.checked})} /></td>
                              <td>
                                <button className="refresh-btn small" onClick={saveContact} disabled={contactBusy}>Save</button>
                                <button className="refresh-btn small" style={{background:'#94a3b8', marginLeft:4}} onClick={() => setContactEdit(null)} disabled={contactBusy}>Cancel</button>
                              </td>
                            </tr>
                          )}
                          {myContacts.map(c => contactEdit && contactEdit.id === c.id ? (
                            <tr key={c.id} style={{background:'#fef3c7'}}>
                              <td><input value={contactEdit.email} onChange={e=>setContactEdit({...contactEdit, email:e.target.value})} style={{width:'100%'}} /></td>
                              <td><input value={contactEdit.name||''} onChange={e=>setContactEdit({...contactEdit, name:e.target.value})} style={{width:'100%'}} /></td>
                              <td><input value={contactEdit.role||''} onChange={e=>setContactEdit({...contactEdit, role:e.target.value})} style={{width:'100%'}} /></td>
                              <td className="num"><input type="checkbox" checked={!!contactEdit.is_primary} onChange={e=>setContactEdit({...contactEdit, is_primary:e.target.checked})} /></td>
                              <td>
                                <button className="refresh-btn small" onClick={saveContact} disabled={contactBusy}>Save</button>
                                <button className="refresh-btn small" style={{background:'#94a3b8', marginLeft:4}} onClick={() => setContactEdit(null)} disabled={contactBusy}>Cancel</button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={c.id}>
                              <td><code>{c.email}</code>{c.is_primary && <span className="small" style={{marginLeft:4, color:'#15803d', fontWeight:600}}>★</span>}</td>
                              <td className="small">{c.name || ''}</td>
                              <td className="small">{c.role || ''}</td>
                              <td className="num">{c.is_primary ? '✓' : ''}</td>
                              <td>
                                <button className="refresh-btn small" onClick={() => setContactEdit({ ...c })} disabled={!!contactEdit || contactBusy}>Edit</button>
                                <button className="refresh-btn small" style={{background:'#b91c1c', marginLeft:4}} onClick={() => delContact(c.id)} disabled={!!contactEdit || contactBusy}>×</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Compliance-certificate recipients (separate list from statement contacts). */}
            {edit.canonical_name && <CertRecipientsPanel customerName={edit.display_name} />}
            <div style={{display:'flex', justifyContent:'flex-end', gap:'0.5rem', marginTop:'0.6rem'}}>
              <button className="refresh-btn" onClick={() => setEdit(null)} disabled={busy} style={{background:'#94a3b8'}}>Cancel</button>
              <button className="refresh-btn" onClick={save} disabled={busy} style={{background:'#0369a1'}}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
